import styles from './hoardPDFPage.module.css'
import { Customer, HoardOrder, HoardType } from "../../../state/types"
import { useEffect, useMemo, useState } from 'react'
import { DocumentKey, Employee, useEmployeeLoaders, usePaymentTypeLoaders } from '../../../../administration'
import { useProductHooks, useProductLoaders } from '../../../../products'
import { useDataTypesHooks } from '../../../../../features/data-types'
import { useBranchLoaders } from '../../../../manager'
import { PriceBreakdown, Item, usePriceOutlineHooks } from '../../../../operations'
import { PDFHeader } from '../../../../../features/ui'
import { HoardItemPDFRow } from './types'
import { TableName } from '../../../../../tables'
import { useCustomerLoaders } from '../../../loaders/customerLoaders'
import { useCustomerTypeLoaders } from '../../../loaders/customerTypeLoaders'
import { firstItem } from '../../../../../utils/listUtils'
import { addressHelpers } from '../../../../../features/address'
import { useSalesHooks } from '../../../hooks/salesHooks'
import { useSaleOperationHooks } from '../../../hooks/saleOperationHooks'

type Props = {
    pageIndex: number
    hoard: HoardOrder
    items: Item[]
    pagesAmount: number
}

export const HoardPDFPage = (props: Props) => {
    const { pageIndex, hoard, items, pagesAmount } = props
    const isLastPage = pageIndex === pagesAmount - 1
    
    const [customer, setCustomer] = useState<Customer>()
    const [seller, setSeller] = useState<Employee>()
    const [rows, setRows] = useState<HoardItemPDFRow[]>([])

    const { describeAddress } = addressHelpers
    const { getPriceOutline, getPriceOutlineLive, getPriceOutlineLiveFromSubtotal } = usePriceOutlineHooks()
    const { formatDate, formatDateTime, formatPrice, formatIdentifier, formatPhone } = useDataTypesHooks()
    const { describeMeasure, describeQuantity } = useProductHooks()
    const { getDocumentFooter } = useSalesHooks()
    const { describePaymentItemName } = useSaleOperationHooks()

    const branchLoaders = useBranchLoaders()
    const productLoaders = useProductLoaders()
    const customerLoaders = useCustomerLoaders()
    const customerTypeLoaders = useCustomerTypeLoaders()
    const { findCustomerType } = customerTypeLoaders()
    const employeeLoaders = useEmployeeLoaders()
    const paymentTypeLoaders = usePaymentTypeLoaders()

    const branch = useMemo(() => branchLoaders().findBranch(hoard.branchId)!, [hoard])
    const isProductHoard = hoard.type === HoardType.PRODUCT
    const documentFooter = useMemo(() => getDocumentFooter(DocumentKey.HOARD), [getDocumentFooter])

    const priceOutline = useMemo(() => getPriceOutline(TableName.HOARD_ORDERS, hoard), [hoard])
    const itemsPriceOutline = useMemo(() => hoard.type === HoardType.PRODUCT ?
        getPriceOutlineLive(hoard.items) :
        getPriceOutlineLiveFromSubtotal(hoard.value)
    , [hoard])
    
    const columns = [
        { name: 'product', label: 'Producto' },
        { name: 'measure', label: 'Medida' },
        { name: 'unitTotal', label: 'P. Unitario' },
        { name: 'quantity', label: 'Cantidad' },
        { name: 'total', label: 'P. Total' },
    ]

    const loadRows = async () => {
        const productIds = items.map(item => item.productId)
        const { loadProducts } = productLoaders()
        const products = await loadProducts(productIds)

        return itemsPriceOutline.items.length > 0 ? items.map(item => {
            const product = products.find(product => product.id === item.productId)!
            const itemPriceOutline = itemsPriceOutline.items.find(itemPriceOutline => itemPriceOutline.productId === product.id)!
            return {
                product: product.name,
                measure: describeMeasure(product),
                unitSubtotal: formatPrice(itemPriceOutline.unitSubtotal),
                unitTotal: formatPrice(itemPriceOutline.unitTotal),
                quantity: describeQuantity(item.quantity, product),
                total: formatPrice(itemPriceOutline.total)
            }
        }) : []
    }

    useEffect(() => {
        employeeLoaders().loadEmployee(hoard.sellerId).then(setSeller)
        customerLoaders().loadCustomer(hoard.customerId).then(setCustomer)
        loadRows().then(newRows => setRows(newRows))
    }, [hoard, items, itemsPriceOutline])

    return (
        <div className={styles.container}>
            <div className={styles.body}>
                <PDFHeader noValidTicketLabel />

                {pageIndex === 0 && (<>
                    <div className={styles.row}>
                        <span className={styles.hoardTitle}>
                            Acopio #{hoard.code}
                        </span>
                        <div className={styles.date}>
                            Fecha: {formatDateTime(new Date(hoard.creationDate))}
                        </div>
                    </div>

                    <div className={`${styles.row} ${styles.alignStart}`}>
                        <div className={styles.infoSection}>
                            <span>Cliente: {customer?.name}</span>
                            <span>Dirección: {customer?.mainAddress?.address || '-'}</span>
                            <span>DNI/CUIT: {customer ? formatIdentifier(customer.identifier) : '-'}</span>
                            <span>Teléfono: {customer && customer.phones.length > 0 ? formatPhone(firstItem(customer!.phones)) : '-'}</span>
                        </div>
                        <div className={`${styles.infoSection} ${styles.rightSection}`}>
                            <span>Vendedor: {seller?.name}</span>
                            <span>Teléfono: {seller?.phone ? formatPhone(seller.phone) : '-'}</span>
                        </div>
                    </div>

                    <div className={`${styles.row} ${styles.alignStart}`}>
                        <div className={styles.infoSection}>
                            <span>Sucursal: {branch.name}</span>
                            <span>Dirección: {describeAddress(branch.address)}</span>
                        </div>
                        <div className={`${styles.infoSection} ${styles.rightSection}`}>
                            <span>Fecha Límite de Retiro: {formatDate(new Date(hoard.dueDate))}</span>
                        </div>
                    </div>

                    <div className={`${styles.row} ${styles.alignStart}`}>
                        <div className={styles.infoSection}>
                            <span>
                                {`Medio${hoard.paymentItems.length > 1 ? 's' : ''} de Pago: `}
                                {hoard.paymentItems.length > 0 ?
                                    hoard.paymentItems.map(paymentItem => {
                                        const name = describePaymentItemName(paymentItem)
                                        const amount = hoard.paymentItems.length > 1 ? ` (${formatPrice(paymentItem.amount)})` : ''
                                        return `${name}${amount}`
                                    }).join(', ') :
                                    paymentTypeLoaders().findPaymentType(hoard.paymentTypeId)?.name || 'Sin medio de pago'
                                }
                            </span>
                        </div>
                        <div className={`${styles.infoSection} ${styles.rightSection}`}>
                            <span>Lista de Precios: {findCustomerType(hoard.customerTypeId)!.name}</span>
                        </div>
                    </div>

                    {!isProductHoard && (
                        <div className={`${styles.row} ${styles.alignStart}`}>
                            <div className={styles.infoSection}>
                                <span>Valor Acopiado: {formatPrice(hoard.value)}</span>
                            </div>
                        </div>
                    )}
                </>)}

                {rows.length > 0 && (
                    <>
                        <div className={styles.tableRow}>
                            <table className={styles.table}>
                                <thead>
                                    <tr>
                                        {columns.map((column, index) => (
                                            <th key={index}>
                                                {column.label}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                        {rows.map((row, index) => (
                                            <tr key={index}>
                                                {columns.map((column, index) => (
                                                    <td key={index}>
                                                        {row[column.name as keyof HoardItemPDFRow]}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                </tbody>
                            </table>
                        </div>
                        <div className={styles.pagination}>
                            {pageIndex + 1} / {pagesAmount}
                        </div>
                    </>
                )}

                {!isProductHoard && (
                    <div className={styles.separator} />
                )}
                
                {isLastPage && priceOutline && (
                    <div className={`${styles.row} ${styles.priceOutline}`}>
                        <PriceBreakdown data={priceOutline} showDetail={false} />
                    </div>
                )}

                <div className={styles.footer}>
                    {isLastPage && hoard.notes && (
                        <div className={styles.row}>
                            <span className={styles.notes}>Notas: {hoard.notes}</span>
                        </div>
                    )}
                    <div className={styles.separator} />
                    <div className={`${styles.row} ${styles.footerRow}`}>
                        <span>
                            Sucursal {branch.name}: {describeAddress(branch.address)}{branch.email ? ` - Mail: ${branch.email}` : ''}
                        </span>
                        <span>
                            {documentFooter}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    )
}
